import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Download, Eye, Sparkles, Pencil } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { DocContentView, GenerateDocumentDialog, StatusBadge, downloadApiFile } from "@/components/shared/DocKit";

export default function Documents() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [generated, setGenerated] = useState(null);
  const [docs, setDocs] = useState(null);
  const [genOpen, setGenOpen] = useState(false);
  const [viewDoc, setViewDoc] = useState(null);

  const load = useCallback(() => {
    api.get("/docs/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/docs/generated").then((r) => setGenerated(r.data)).catch(() => setGenerated([]));
    api.get("/docs/documents").then((r) => setDocs(r.data)).catch(() => setDocs([]));
  }, []);
  useEffect(load, [load]);

  const openManaged = async (d) => {
    if (d.file_id) {
      try {
        await downloadApiFile(`/files/${d.file_id}/download`, d.title);
      } catch {
        toast.error(t("Download fehlgeschlagen", "Download failed"));
      }
      return;
    }
    const full = await api.get(`/docs/documents/${d.id}`);
    setViewDoc(full.data);
  };

  const catLabel = (id) => meta?.categories.find((c) => c.id === id)?.label || id;

  return (
    <div className="space-y-8" data-testid="documents-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">{t("Dokumente", "Documents")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("Aus Vorlagen erstellen, bearbeiten und als PDF exportieren", "Create from templates, edit and export as PDF")}</p>
        </div>
        {meta?.can_generate && (
          <Button className="rounded-[10px] bg-[#0B1220]" onClick={() => setGenOpen(true)} data-testid="open-generator-button">
            <Sparkles className="h-4 w-4 mr-1.5" /> {t("Dokument aus Vorlage erstellen", "Create from template")}
          </Button>
        )}
      </div>

      {/* generated documents */}
      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{t("Meine generierten Dokumente", "My generated documents")}</h2>
        {!generated ? (
          <Skeleton className="mt-3 h-32 rounded-[16px]" />
        ) : generated.length === 0 ? (
          <Card className="mt-3 rounded-[16px]"><CardContent className="py-10 text-center text-sm text-muted-foreground">{t("Noch keine generierten Dokumente. Erstellen Sie eines aus einer Vorlage.", "No generated documents yet. Create one from a template.")}</CardContent></Card>
        ) : (
          <div className="mt-3 space-y-3" data-testid="generated-list">
            {generated.map((g) => (
              <Card key={g.id} className="rounded-[14px]" data-testid={`generated-row-${g.id}`}>
                <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{g.title}</p>
                    <div className="mt-1 flex items-center gap-2">
                      <StatusBadge status={g.status} />
                      <Badge variant="secondary" className="rounded-full text-[10px]">v{g.version}</Badge>
                      <span className="text-[11px] text-muted-foreground">{t("Vorlage", "Template")}: {g.template_name}</span>
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button size="sm" variant="outline" className="rounded-[10px]" onClick={() => navigate(`/portal/dokumente/g/${g.id}`)} data-testid={`generated-open-${g.id}`}>
                      <Pencil className="h-3.5 w-3.5 mr-1.5" /> {t("\u00d6ffnen", "Open")}
                    </Button>
                    <Button size="sm" className="rounded-[10px] bg-[#0B1220]" onClick={() => downloadApiFile(`/docs/generated/${g.id}/pdf`, `${g.title}.pdf`).catch(() => toast.error("PDF-Fehler"))} data-testid={`generated-pdf-${g.id}`}>
                      <Download className="h-3.5 w-3.5 mr-1.5" /> PDF
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* published managed documents */}
      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{t("Freigegebene Unterlagen", "Approved documents")}</h2>
        {!docs ? (
          <Skeleton className="mt-3 h-32 rounded-[16px]" />
        ) : docs.length === 0 ? (
          <Card className="mt-3 rounded-[16px]"><CardContent className="py-10 text-center text-sm text-muted-foreground">{t("Keine Unterlagen verf\u00fcgbar.", "No documents available.")}</CardContent></Card>
        ) : (
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="managed-docs-list">
            {docs.map((d) => (
              <Card key={d.id} className="rounded-[16px] card-hover" data-testid={`managed-doc-${d.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="h-10 w-10 rounded-[12px] bg-[#0B1220] flex items-center justify-center">
                      <FileText className="h-5 w-5 text-[#D6C08D]" />
                    </div>
                    <Badge variant="secondary" className="rounded-full text-[10px]">v{d.version}</Badge>
                  </div>
                  <h3 className="mt-4 text-sm font-semibold leading-snug">{d.title}</h3>
                  <p className="mt-1 text-xs text-muted-foreground">{catLabel(d.category)}</p>
                  <Button size="sm" variant="outline" className="mt-4 w-full rounded-[10px]" onClick={() => openManaged(d)} data-testid={`managed-doc-open-${d.id}`}>
                    {d.file_id ? <><Download className="h-3.5 w-3.5 mr-1.5" /> Download</> : <><Eye className="h-3.5 w-3.5 mr-1.5" /> {t("Ansehen", "View")}</>}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>

      <GenerateDocumentDialog open={genOpen} onOpenChange={setGenOpen} onCreated={(doc) => navigate(`/portal/dokumente/g/${doc.id}`)} />

      <Dialog open={!!viewDoc} onOpenChange={(o) => !o && setViewDoc(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          {viewDoc && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl">{viewDoc.title}</DialogTitle>
                <DialogDescription>{catLabel(viewDoc.category)} {"\u00b7"} v{viewDoc.version}</DialogDescription>
              </DialogHeader>
              <DocContentView content={viewDoc.content} />
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
